// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useState } from 'react';
import * as d3 from 'd3';
import { feature as topoFeature } from 'topojson-client';
import { HVA_TO_REGION_MAP } from '../lib/models';
import { REGION_LOOKUP } from '../../../data/regions';

// Stronger typing for features
type HVAFeature = GeoJSON.Feature<GeoJSON.Geometry, { id: string; name_fi?: string; name_sv?: string }>;

interface FinlandMapProps {
  selectedRegions: string[];
  onRegionToggle: (regionKey: string) => void;
  coverageData: { [regionKey: string]: number };
  'aria-label'?: string;
}

export const FinlandMap: React.FC<FinlandMapProps> = ({
  selectedRegions,
  onRegionToggle,
  coverageData,
  'aria-label': ariaLabel,
}) => {
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);

  // NEW state for geo features + sizing
  const [features, setFeatures] = useState<HVAFeature[]>([]);
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 700, h: 450 });

  // Safer data load + object name guard + error handling
  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const res = await fetch('/data/hva.json');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const topoOrGeo = await res.json();

        const isTopo = topoOrGeo?.type === 'Topology';
        const geo = isTopo
          ? (() => {
              const obj =
                topoOrGeo.objects?.hva ??
                (topoOrGeo.objects ? Object.values(topoOrGeo.objects)[0] : undefined);
              if (!obj) throw new Error('No TopoJSON objects found');
              return topoFeature(topoOrGeo, obj);
            })()
          : topoOrGeo;

        if (!mounted) return;
        setFeatures(
          (geo as GeoJSON.FeatureCollection).features
            .slice()
            // Bigger areas first, small islands last for crisp edges
            .sort((a, b) => d3.geoArea(a as any) - d3.geoArea(b as any)) as HVAFeature[]
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load HVA boundaries:', err);
        if (mounted) setFeatures([]);
      }
    })();

    return () => { mounted = false; };
  }, []);

  // Guard ResizeObserver for SSR
  React.useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setSize({ w: entry.contentRect.width, h: 450 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Optional: validate IDs early (helps spot mismatches)
  React.useEffect(() => {
    if (!features.length) return;
    const hvaIds = features.map(f => (f.properties as any).id);
    const unknown = hvaIds.filter((id: string) => !(id in HVA_TO_REGION_MAP));
    
    // Debug logging removed - mapping is now fixed
    
    if (unknown.length) {
      // eslint-disable-next-line no-console
      console.warn('Unmapped HVA ids:', unknown);
    }
  }, [features]);

  // Projection memo (micro-optimisation, clarity)
  const projection = React.useMemo(() => {
    const proj = d3.geoConicConformal().parallels([63, 67]).rotate([-27, 0]);
    if (features.length) proj.fitSize([size.w, size.h], { type: 'FeatureCollection', features } as any);
    return proj;
  }, [features, size]);

  const path = React.useMemo(() => d3.geoPath(projection), [projection]);

  // domain for legend from your coverageData if present
  const domain = React.useMemo((): [number, number] => {
    const vals = features
      .map((f: any) => {
        const id = f.properties.id;
        const regionKey = HVA_TO_REGION_MAP[id];
        return regionKey != null ? coverageData[regionKey] : null;
      })
      .filter((v: any): v is number => v != null);

    if (!vals.length) return [0, 100] as [number, number];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    
    // Handle case where all values are the same (including 0)
    if (min === max) {
      if (min === 0) {
        return [0, 100] as [number, number]; // Default range when all coverage is 0
      }
      return [Math.max(0, min - 1), min + 1];
    }
    
    return [min, max];
  }, [features, coverageData]);

  // update getRegionColor to use a simple scale, selection affects both fill and stroke
  const getRegionColor = (regionId: string, isSelected: boolean) => {
    const regionKey = HVA_TO_REGION_MAP[regionId];
    const coverage = regionKey ? coverageData[regionKey] : undefined;
    
    // For selected regions, ALWAYS use a distinct color regardless of coverage
    if (isSelected) {
      return '#f59e0b'; // Amber-500 - very distinct from the teal coverage colors
    }
    
    // For unselected regions, use coverage-based colors or default
    if (coverage == null) {
      return '#334155'; // Default gray for regions without coverage data
    }
    
    // Simple linear interpolation between light and deep teal for unselected regions
    const t = (coverage - domain[0]) / (domain[1] - domain[0]);
    const r = Math.round(167 + (15 - 167) * t); // #a7 -> #0f
    const g = Math.round(243 + (118 - 243) * t); // #f3 -> #76
    const b = Math.round(208 + (110 - 208) * t); // #d0 -> #6e
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleRegionClick = (regionId: string) => {
    const regionKey = HVA_TO_REGION_MAP[regionId];
    if (regionKey) {
      onRegionToggle(regionKey);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent, region: { id: string; name: string }) => {
    const regionKey = HVA_TO_REGION_MAP[region.id];
    const isSelected = regionKey ? selectedRegions.includes(regionKey) : false;

    const rect = containerRef.current?.getBoundingClientRect();
    const left = e.clientX - (rect?.left ?? 0);
    const top  = e.clientY - (rect?.top ?? 0);

    setTip({
      x: left,
      y: top,
      text: `${region.name}${isSelected ? ' (selected)' : ''}`,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!tip) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const left = e.clientX - (rect?.left ?? 0);
    const top  = e.clientY - (rect?.top ?? 0);
    setTip((t) => (t ? { ...t, x: left, y: top } : t));
  };

  const handleMouseLeave = () => setTip(null);

  const handleKeyDown = (e: React.KeyboardEvent, regionId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRegionClick(regionId);
    }
  };

  return (
    <div ref={containerRef} className="vaccine-demo__finland-map-container">
      <svg
        ref={svgRef}                // NEW
        width="100%"
        height={size.h}             // NEW
        viewBox={`0 0 ${size.w} ${size.h}`}  // NEW
        className="vaccine-demo__finland-map"
        role="img"
        aria-label={ariaLabel || "Interactive Finland map showing regional coverage"}
        onMouseMove={handleMouseMove}
      >
        <g>
           {features.map((f: any) => {
             const id = f.properties.id;
             const regionKey = HVA_TO_REGION_MAP[id];
             // Use canonical region name from our dataset to ensure proper encoding
             const name = regionKey && REGION_LOOKUP[regionKey] 
               ? REGION_LOOKUP[regionKey].name 
               : (f.properties.name_fi || id);
             const isSelected = regionKey ? selectedRegions.includes(regionKey) : false;
             
             // Debug logging removed - mapping is now fixed
             
             return (
               <path
                 key={id}
                 d={(path as any)(f)}
                 fill={getRegionColor(id, isSelected)}
                 stroke={isSelected ? "#d97706" : "#475569"}
                 strokeWidth={isSelected ? 4 : 0.8}
                 strokeDasharray={isSelected ? "8,4" : "none"}
                 className="vaccine-demo__region-path cursor-pointer"
                 role="button"
                 tabIndex={0}
                 aria-pressed={isSelected}
                 aria-label={`${name}${isSelected ? " (selected)" : ""}. Click to ${isSelected ? 'deselect' : 'select'}.`}
                 onClick={() => handleRegionClick(id)}
                 onKeyDown={(e) => handleKeyDown(e, id)}
                 onMouseEnter={(e) => handleMouseEnter(e, { id, name })}
                 onMouseLeave={handleMouseLeave}
                 onFocus={(e) => e.currentTarget.setAttribute('filter', 'url(#focus-shadow)')}
                 onBlur={(e) => e.currentTarget.removeAttribute('filter')}
               >
                 <title>{name}</title>
               </path>
             );
          })}
        </g>
        <defs>
          <filter id="focus-shadow">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#0b1324" floodOpacity="0.6"/>
          </filter>
        </defs>
      </svg>

      {/* Tooltip */}
      {tip && (
        <div
          className="vaccine-demo__map-tooltip"
          style={{ left: tip.x, top: tip.y }}
        >
          {tip.text}
        </div>
      )}

    </div>
  );
};